import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from 'helpers/api';

const rawBaseQuery = createBaseQuery('billing', { timeout: 0 });

const baseQuery = async (args, api, extraOptions) => {
  const response = await rawBaseQuery(args, api, extraOptions);
  if (response.error || !response.data?.error) return response;

  return { error: { status: response.data.status || 200, data: response.data } };
};

export const billingEntityOptionSetFromStructure = structure => {
  if (structure?.billing_mode !== 'entity' || !structure.entity_option_set?.options?.length) {
    return null;
  }

  return {
    name: structure.entity_option_set.name,
    options: structure.entity_option_set.options,
  };
};

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery,
  tagTypes: ['BillingStructure'],
  endpoints: builder => ({
    getBillingEntity: builder.query({
      query: () => '/',
    }),
    updateBillingEntityAddress: builder.mutation({
      query: params => ({
        url: '/',
        method: 'PUT',
        body: params,
      }),
      invalidatesTags: ['BillingStructure'],
    }),
    getBillingStructure: builder.query({
      query: () => '/structure',
      transformResponse: ({ result }) => result,
      providesTags: ['BillingStructure'],
    }),
    updateBillingStructure: builder.mutation({
      query: body => ({
        url: '/structure',
        method: 'PUT',
        body,
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: ['BillingStructure'],
    }),
    updateBillingEntity: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/entities/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: ['BillingStructure'],
    }),
    saveBillingEntityOptionSet: builder.mutation({
      query: body => ({
        url: '/entity_option_set',
        method: 'PUT',
        body,
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: ['BillingStructure'],
    }),
    deleteBillingEntityOptionSet: builder.mutation({
      query: () => ({
        url: '/entity_option_set',
        method: 'DELETE',
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: ['BillingStructure'],
    }),
    deleteBillingEntityOption: builder.mutation({
      query: id => ({
        url: `/entity_option_set/options/${id}`,
        method: 'DELETE',
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: ['BillingStructure'],
    }),
  }),
});

export const {
  useLazyGetBillingEntityQuery,
  useUpdateBillingEntityAddressMutation,
  useGetBillingStructureQuery,
  useUpdateBillingStructureMutation,
  useUpdateBillingEntityMutation,
  useSaveBillingEntityOptionSetMutation,
  useDeleteBillingEntityOptionSetMutation,
  useDeleteBillingEntityOptionMutation,
} = billingApi;

export const useBillingEntityOptionSet = () => {
  const { data } = useGetBillingStructureQuery();
  return billingEntityOptionSetFromStructure(data);
};
